import type { StoreState } from '@deqo/foundation';
import { Arr, memone, NullFunction, VoidCallback } from '@deqo/foundation';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, ObjectManager } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { MouseInput } from '../../../platform/input/MouseInput';
import type { CameraState } from '../../canvas/Camera';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, PotentialInteraction, RelativeTo } from '../../canvas/entities/Physical';
import { MyProfileInfo } from '../../canvas/MyProfileInfo';
import { PointerInfo } from '../../canvas/PointerInfo';
import { Selection } from '../../canvas/Selection';
import { ToolBehavior } from '../../canvas/tooling/ToolBehavior';
import { Pose } from '../../canvas/visualization/Pose';
import { resolve } from '../../Container';
import { Audited } from '../auditing/Audited';
import { Concealable } from '../conceal/Concealable';
import { FeatureManager } from '../feature/FeatureManager';
import { Hand } from './Hand';
import { handPlay } from './HandCommands';
import { HandControl } from './HandControl';
import { HandInfo } from './HandInfo';
import { HandLayout } from './HandLayout';
import { HoldLayout } from './HoldLayout';

const topLeftSorter = (a: Physical, b: Physical) => {

    const ab = a.bounds(RelativeTo.Model);
    const bb = b.bounds(RelativeTo.Model);

    if (bb.top > ab.bottom) return -1;

    const area = Rect.fromPoints([ab.topLeft(), Point.max]);
    if (area.intersects(bb)) {
        return -1;
    }
    else {
        return 1;
    }
};

export const leftTopSorter = (a: Physical, b: Physical) => {

    const ab = a.bounds(RelativeTo.Model);
    const bb = b.bounds(RelativeTo.Model);

    if (bb.left > ab.right) return -1;

    const area = Rect.fromPoints([ab.topLeft(), Point.max]);
    if (area.intersects(bb)) {
        return -1;
    }
    else {
        return 1;
    }
};

export class HandToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera: Camera;
    @resolve(CommandDispatcher) private dispatch: CommandDispatcher;
    @resolve(FeatureManager) private features!: FeatureManager;
    @resolve(EditorSession) private session: EditorSession;
    @resolve(HandControl) private control: HandControl;
    @resolve(HandLayout) private handLayout: HandLayout;
    @resolve(HoldLayout) private holdLayout: HoldLayout;
    @resolve(ObjectManager) private objects: ObjectManager;

    private boundDispatch: VoidCallback = NullFunction;

    public onMouseDown(state: string, mi: MouseInput): string | void {
        if (!this.features.get('Hands')) return;

        if (state === 'idle') {
            const target = this.findTargetAt(mi.position);
            if (target) {
                // if (mi.count === 2 && this.maybeMutate(target)) {
                if (mi.count === 2) {
                    this.dispatch(handPlay, [target.id]);
                    return 'idleOnUp';
                }
                else {
                    const vpt = mi.position;
                    this.control.mutate({ target, drag: [vpt, vpt] });
                    return 'handing';
                }
            }
        }
    }

    public onMouseMove(state: string, e: MouseInput): string | void {
        if (!this.features.get('Hands')) return;
        const vpt = e.position;

        if (state === 'idle') {
            const prev = this.control.target;
            const next = this.findTargetAt(e.position);
            if (prev !== next) {
                if (prev) Pose.update(prev, { targeted: false });
                this.control.mutate({ target: next });
                if (next) Pose.update(next, { targeted: true });
            }
        }
        if (state === 'handing') {
            const area = this.getOuterHandArea(this.camera.getState());
            if (area.contains(vpt)) {
                if (this.insertIfRequired([this.control.target], vpt)) {
                    this.control.mutate(x => x.drag = [x.drag[1], vpt]);
                }
                else {
                    this.control.mutate(x => x.drag = [x.drag[0], vpt]);
                }
            }
            else {
                const target = this.control.target.cast(Physical);
                const mpt = this.camera.toModel(e.position);

                this.objects.update(() => {
                    const root = this.objects.getRoot();
                    root.add(target.removeSelf());
                    target.moveTo(mpt.subtract(target.size().divide(2)));
                    if (target.is(Audited)) {
                        target.set('userId', this.session.get(MyProfileInfo).id);
                    }
                    if (target.is(Concealable)) {
                        target.set('concealed', true);
                    }
                });

                this.control.mutate({ target: null, drag: null });
                this.session.apply(Selection, x => x.replace([target]));

                // Swap to SelectionTransformToolBehavior
                return 'transformPrimed';
            }
        }
        if (state === 'transforming') {
            const selection = this.session.get(Selection);
            if (selection.size < 1 || selection.size > 20) return;
            if (!this.handLayout.test(vpt)) return;

            const targets = selection.polarize(Physical).sort(leftTopSorter);
            this.session.apply(Selection, x => x.clear());

            const hand = this.getMyHand();
            const [side, sideOf] = this.findInsertPoint(vpt) ?? [];

            this.objects.update(() => {
                for (const t of targets) {
                    t.removeSelf();
                    if (t.is(Concealable)) {
                        t.set('concealed', false);
                    }
                    if (side === 'left') {
                        hand.addBefore(sideOf, t);
                    }
                    else if (side === 'right') {
                        hand.addAfter(sideOf, t);
                    }
                    else {
                        hand.add(t);
                    }
                    t.moveTo(0, 0);
                }
            });

            if (targets.length === 1) {
                this.control.mutate({ target: targets[0], drag: [vpt, vpt] });
            }
            else {
                this.control.mutate({ target: null, drag: null });
            }

            return 'idleOnUp';
        }
    }

    public onMouseUp(state: string, e: MouseInput): string | void {
        if (!this.features.get('Hands')) return;

        if (state === 'handing') {
            this.control.mutate({ drag: null });
            const target = this.findTargetAt(e.position);
            this.control.mutate({ target });
            return 'idle';
        }
        if (state === 'handmuting') {
            this.boundDispatch!();
            this.boundDispatch = NullFunction;
            return 'idle';
        }

        if (state === 'idleOnUp') {
            return 'idle';
        }
    }

    private findTargetAt(vpt: Point) {
        // Check look first
        const hold = this.holdLayout.getState();
        const priority = this.getLookedAtItem(hold);
        const hit = (
            (priority && priority.lookOutline.contains(vpt) ? priority : null) ??
            (hold.slice().reverse().find(x => x.holdOutline.contains(vpt)))
        );
        return this.objects.getMaybe(hit?.id, Physical);
    }

    private findInsertPoint(vpt: Point) {
        const hold = this.holdLayout.getState();
        if (!hold.length) return null;
        const measurements = hold.map(x => [x, x.holdOutline.distanceTo(vpt)] as const);
        const [result] = Arr.sortBy(measurements, 1) ?? [];
        const [item] = result ?? [];
        if (!item) return null;
        const side = item.holdOutline.center.x < vpt.x ? 'right' : 'left';
        const object = this.objects.getMaybe(item.id, Physical);
        return [side, object] as const;
    }

    private insertIfRequired(targets: Physical[], vpt: Point) {
        let inserted = false;
        const [side, sideOf] = this.findInsertPoint(vpt) ?? [];
        if (!side) return false;
        const hand = this.getMyHand();
        this.objects.update(() => {
            for (const t of targets) {
                // Is already in hand?
                if (t.parent === hand) {
                    // If inserting onto self or alread in correct position, skip
                    if (t === sideOf) continue;
                    if (side === 'left' && t.nextSibling() === sideOf) continue;
                    if (side === 'right' && t.prevSibling() === sideOf) continue;
                }
                t.removeSelf();
                if (t.is(Concealable)) {
                    t.set('concealed', false);
                }
                if (side === 'left') {
                    hand.addBefore(sideOf, t);
                }
                else if (side === 'right') {
                    hand.addAfter(sideOf, t);
                }
                else {
                    hand.add(t);
                }
                t.moveTo(0, 0);
                inserted = true;
            }
        });
        return inserted;
    }

    private maybeMutate(target: Physical) {
        const { transform } = this.holdLayout.find(x => x.id === target.id);
        const rpt = transform.inverse().apply(this.session.get(PointerInfo).position);
        const [hit, hint] = target.testInteraction(PotentialInteraction('mutate', rpt, false));
        // If positive test, return result.
        if (!hit) return false;
        // If passed, check there is an mutation command
        const { mutation } = Component.describe(target);
        if (!mutation) return false;
        // If there is, bind for dispatch and return true
        this.boundDispatch = () => this.dispatch(mutation, [target.id, hint]);
        return true;
    }

    private getMyHand() {
        const { object } = this.session.get(HandInfo);
        return this.objects.getMaybe(object, Hand);
    }

    @memone()
    private getLookedAtItem(ls: StoreState<HoldLayout>) {
        return ls.find(x => !!x.lookOutline);
    }

    @memone()
    private getOuterHandArea(cs: CameraState) {
        return new Rect(0, cs.height - 200, cs.width, 200);
    }
}
